import { useState } from 'react'
import { X } from 'lucide-react'
import { createPlan } from '../services/api'
import Loader from './Loader'

const CreatePlanModal = ({ onClose, onSuccess }) => {
	const [formData, setFormData] = useState({
		planDefault: false,
		availability: true,
		visibility: true,
		typeAccount: '',
		name: '',
		description: '',
		benefits: '',
		timeDefault: '0',
		daysUntilFirstPayment: '1',
		price: '',
		pricePix: '',
		priceTed: '',
		priceBillet: '',
		priceP2P: '',
		priceGenerateDynamicQrCodePix: '',
		priceGenerateStaticQrCodePix: '',
		priceReceiveDynamicQrCodePix: '',
		priceReceiveStaticQrCodePix: '',
		billSettlementPrice: '',
		priceWithdraw: '',
		quantityGenerateDynamicQrCodePix: '',
		quantityGenerateStaticQrCodePix: '',
		quantityReceiveDynamicQrCodePix: '',
		quantityReceiveStaticQrCodePix: '',
		quantityPix: '',
		quantityTed: '',
		quantityBillet: '',
		quantityPeriodo: '0',
		quantityP2P: '',
		quantityBillSettlement: ''
	})
	const [contractFile, setContractFile] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState('')

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}))
		setError('')
	}

	const handleFileChange = (e) => {
		setContractFile(e.target.files[0])
		setError('')
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setIsLoading(true)
		setError('')

		try {
			const formDataToSend = new FormData()
			
			// Append all form fields
			Object.keys(formData).forEach(key => {
				if (formData[key] !== '') {
					formDataToSend.append(key, formData[key])
				}
			})

			// Append contract file if selected
			if (contractFile) {
				formDataToSend.append('contract', contractFile)
			}

			await createPlan(formDataToSend)
			onSuccess()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
			<div className="relative top-10 mx-auto p-6 border-0 w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-2xl rounded-2xl bg-white">
				<div className="mt-3">
					<div className="flex items-center justify-between mb-6">
						<h3 className="text-2xl font-bold text-gray-900">Create New Plan</h3>
						<button
							onClick={onClose}
							className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-full"
						>
							<X className="h-6 w-6" />
						</button>
					</div>

					<form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
						{/* Basic Information */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
								<input
									type="text"
									name="name"
									required
									value={formData.name}
									onChange={handleChange}
									className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
									placeholder="Enter plan name"
								/>
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">Type Account *</label>
								<select
									name="typeAccount"
									required
									value={formData.typeAccount}
									onChange={handleChange}
									className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
								>
									<option value="">Select type</option>
									<option value="PF">Pessoa Física (PF)</option>
									<option value="PJ">Pessoa Jurídica (PJ)</option>
								</select>
							</div>
						</div>

						<div>
							<label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
							<textarea
								name="description"
								required
								rows={4}
								value={formData.description}
								onChange={handleChange}
								className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md resize-none"
								placeholder="Enter plan description"
							/>
						</div>

						<div>
							<label className="block text-sm font-semibold text-gray-700 mb-2">Benefits</label>
							<textarea
								name="benefits"
								rows={4}
								value={formData.benefits}
								onChange={handleChange}
								className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md resize-none"
								placeholder="Enter plan benefits"
							/>
						</div>

						{/* Settings */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">Availability</label>
								<select
									name="availability"
									value={formData.availability}
									onChange={handleChange}
									className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
								>
									<option value={true}>Available</option>
									<option value={false}>Unavailable</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">Visibility</label>
								<select
									name="visibility"
									value={formData.visibility}
									onChange={handleChange}
									className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
								>
									<option value={true}>Visible</option>
									<option value={false}>Hidden</option>
								</select>
							</div>
						</div>

						{/* Configuration */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">Time Default (days)</label>
								<input
									type="number"
									name="timeDefault"
									value={formData.timeDefault}
									onChange={handleChange}
									className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
									placeholder="0"
								/>
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">Days Until First Payment</label>
								<input
									type="number"
									name="daysUntilFirstPayment"
									value={formData.daysUntilFirstPayment}
									onChange={handleChange}
									className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
									placeholder="1"
								/>
							</div>
						</div>

						{/* Pricing */}
						<div className="space-y-6">
							<h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Pricing Configuration</h3>
							
							{/* Basic Prices */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Base Price</label>
									<input
										type="number"
										step="0.01"
										name="price"
										value={formData.price}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">PIX Price</label>
									<input
										type="number"
										step="0.01"
										name="pricePix"
										value={formData.pricePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
							</div>

							{/* Transfer Prices */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">TED Price</label>
									<input
										type="number"
										step="0.01"
										name="priceTed"
										value={formData.priceTed}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Billet Price</label>
									<input
										type="number"
										step="0.01"
										name="priceBillet"
										value={formData.priceBillet}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
							</div>

							{/* P2P and Withdraw */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">P2P Price</label>
									<input
										type="number"
										step="0.01"
										name="priceP2P"
										value={formData.priceP2P}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Withdraw Price</label>
									<input
										type="number"
										step="0.01"
										name="priceWithdraw"
										value={formData.priceWithdraw}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
							</div>

							{/* QR Code Prices */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Generate Dynamic QR Code PIX</label>
									<input
										type="number"
										step="0.01"
										name="priceGenerateDynamicQrCodePix"
										value={formData.priceGenerateDynamicQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Generate Static QR Code PIX</label>
									<input
										type="number"
										step="0.01"
										name="priceGenerateStaticQrCodePix"
										value={formData.priceGenerateStaticQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
							</div>

							{/* Receive QR Code Prices */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Receive Dynamic QR Code PIX</label>
									<input
										type="number"
										step="0.01"
										name="priceReceiveDynamicQrCodePix"
										value={formData.priceReceiveDynamicQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Receive Static QR Code PIX</label>
									<input
										type="number"
										step="0.01"
										name="priceReceiveStaticQrCodePix"
										value={formData.priceReceiveStaticQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
							</div>

							{/* Settlement Price */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Bill Settlement Price</label>
									<input
										type="number"
										step="0.01"
										name="billSettlementPrice"
										value={formData.billSettlementPrice}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0.00"
									/>
								</div>
							</div>
						</div>

						{/* Contract File */}
						<div>
							<label className="block text-sm font-semibold text-gray-700 mb-2">Contract (PDF)</label>
							<input
								type="file"
								accept=".pdf"
								onChange={handleFileChange}
								className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
							/>
						</div>

						{/* Quantities */}
						<div className="space-y-6">
							<h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Quantity Limits</h3>
							
							{/* Basic Quantities */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">PIX Quantity</label>
									<input
										type="number"
										name="quantityPix"
										value={formData.quantityPix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">TED Quantity</label>
									<input
										type="number"
										name="quantityTed"
										value={formData.quantityTed}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
							</div>

							{/* Billet and P2P */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Billet Quantity</label>
									<input
										type="number"
										name="quantityBillet"
										value={formData.quantityBillet}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">P2P Quantity</label>
									<input
										type="number"
										name="quantityP2P"
										value={formData.quantityP2P}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
							</div>

							{/* QR Code Quantities */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Generate Dynamic QR Code PIX</label>
									<input
										type="number"
										name="quantityGenerateDynamicQrCodePix"
										value={formData.quantityGenerateDynamicQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Generate Static QR Code PIX</label>
									<input
										type="number"
										name="quantityGenerateStaticQrCodePix"
										value={formData.quantityGenerateStaticQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
							</div>

							{/* Receive QR Code Quantities */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Receive Dynamic QR Code PIX</label>
									<input
										type="number"
										name="quantityReceiveDynamicQrCodePix"
										value={formData.quantityReceiveDynamicQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Receive Static QR Code PIX</label>
									<input
										type="number"
										name="quantityReceiveStaticQrCodePix"
										value={formData.quantityReceiveStaticQrCodePix}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
							</div>

							{/* Settlement and Period */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Bill Settlement Quantity</label>
									<input
										type="number"
										name="quantityBillSettlement"
										value={formData.quantityBillSettlement}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="10"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Period Quantity</label>
									<input
										type="number"
										name="quantityPeriodo"
										value={formData.quantityPeriodo}
										onChange={handleChange}
										className="w-full px-4 h-[38px] text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
										placeholder="0"
									/>
								</div>
							</div>
						</div>

						{/* Default Plan Checkbox */}
						<div className="flex items-center p-4 bg-gray-50 rounded-xl">
							<input
								type="checkbox"
								name="planDefault"
								checked={formData.planDefault}
								onChange={handleChange}
								className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
							/>
							<label className="ml-3 block text-sm font-medium text-gray-900">
								Set as default plan
							</label>
						</div>

						{error && (
							<div className="rounded-md bg-red-50 p-4">
								<div className="text-sm text-red-700">{error}</div>
							</div>
						)}

						<div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
							<button
								type="button"
								onClick={onClose}
								className="px-6 py-3 border-2 border-gray-300 rounded-xl text-base font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={isLoading}
								className="px-6 py-3 border border-transparent rounded-xl text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
							>
								{isLoading ? (
									<div className="flex items-center">
										<Loader size="sm" />
										<span className="ml-2">Creating...</span>
									</div>
								) : (
									'Create Plan'
								)}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	)
}

export default CreatePlanModal
